import type { ColDef } from "ag-grid-enterprise";
import dayjs from "dayjs";

import type { OrderFormField } from "@web-app/pages/settings/FormBuilderTab/types";
import { CustomField, Order, TableHeadersName, User } from "@web-app/types";
import { getMetersToMiles } from "@web-app/utils/helpers";

import AssigneeCell from "./Table/AssigneeCell";
import CustomFieldCell from "./Table/CustomFieldCell";
import DateCell from "./Table/DateCell";
import DateWindowCell from "./Table/DateWindowCell.tsx";
import DeliveryProviderCell from "./Table/DeliveryProviderCell";
import FulfillmentMethodCell from "./Table/FulfillmentMethodCell";
import PODCell from "./Table/PODCell";
import ReportCountCell from "./Table/ReportCountCell";
import RouteDurationCell from "./Table/RouteDurationCell";
import StatusCell from "./Table/StatusCell";
import WorkspaceCell from "./Table/WorkspaceCell";
import {
  CUSTOM_FIELD_COL_ID_PREFIX,
  applyFormConfigColumnOrder,
} from "./formConfigColumnOrder";

export type TableRow = Order & {
  assignee?: User | null;
  entity: "order" | "route";
  hasAttachments?: boolean;
  reportCount?: number;
};

const calculateRouteDistance = (p: { data: TableRow }) => {
  const distance = p.data?.driverRouteDistance
    ? getMetersToMiles(p.data.driverRouteDistance)
    : p.data?.estimatedDistance
      ? getMetersToMiles(p.data.estimatedDistance)
      : null;

  return distance ? `${distance} miles` : "";
};

const dateCellParams = (p: { data: TableRow }) => ({
  timezone: p.data?.timezone,
});

const orderOnlyValue = (
  p: { data: TableRow },
  getValue: (data: TableRow) => string | null | undefined,
) => (p.data?.entity === "order" ? (getValue(p.data) ?? "") : "");

const INITIAL_TABLE_DATA: ColDef[] = [
  {
    cellRenderer: AssigneeCell,
    enableRowGroup: true,
    headerName: TableHeadersName.Assignee,
    minWidth: 170,
    valueGetter: (p: { data: TableRow }) => p.data?.assignee?.id,
    width: 170,
  },
  {
    cellRenderer: WorkspaceCell,
    colId: "connections",
    enableRowGroup: true,
    headerName: TableHeadersName.Connections,
    minWidth: 200,
    valueGetter: (p: { data: TableRow }) => p.data?.connectedWorkspaceId,
  },
  {
    field: "externalId",
    headerName: TableHeadersName.ExternalId,
    hide: true,
    minWidth: 150,
    width: 150,
  },
  {
    colId: "orderId",
    headerName: TableHeadersName.OrderId,
    hide: true,
    minWidth: 150,
    valueGetter: (p: { data: TableRow }) =>
      orderOnlyValue(p, (data) => data.displayId),
    width: 150,
  },
  {
    colId: "orderInternalId",
    headerName: TableHeadersName.OrderInternalId,
    hide: true,
    minWidth: 150,
    valueGetter: (p: { data: TableRow }) =>
      orderOnlyValue(p, (data) => data.id),
    width: 150,
  },
  {
    colId: "orderSourceId",
    headerName: TableHeadersName.OrderSourceId,
    hide: true,
    minWidth: 150,
    valueGetter: (p: { data: TableRow }) =>
      orderOnlyValue(p, (data) => data.sourceId),
    width: 150,
  },
  {
    field: "priority",
    headerName: TableHeadersName.Priority,
  },
  {
    field: "deliveryTypeText",
    headerName: TableHeadersName.DeliveryType,
    minWidth: 150,
    width: 150,
  },
  {
    cellRenderer: FulfillmentMethodCell,
    headerName: "Fulfillment Method",
    width: 150,
  },
  {
    cellRenderer: DeliveryProviderCell,
    headerName: "Delivery Provider",
    width: 150,
  },

  // Dropoff Information
  {
    field: "dropoff.name",
    headerName: TableHeadersName.RecipientName,
    hide: true,
    minWidth: 150,
    width: 150,
  },
  {
    field: "dropoff.phone",
    headerName: TableHeadersName.RecipientPhone,
    hide: true,
    minWidth: 150,
    width: 150,
  },
  {
    cellClass: "ag-cell-value-style",
    field: "dropoff.note",
    headerName: TableHeadersName.RecipientNote,
    hide: true,
    minWidth: 150,
    width: 150,
  },
  {
    cellClass: "ag-cell-value-style",
    field: "dropoff.businessName",
    headerName: TableHeadersName.RecipientBusinessName,
    hide: true,
    minWidth: 150,
    width: 150,
  },
  {
    autoHeight: true,
    field: "dropoff.address",
    headerName: TableHeadersName.DropoffAddress,
    minWidth: 170,
    width: 200,
    wrapText: true,
  },
  {
    colId: "dropoffCountry",
    headerName: TableHeadersName.DropoffCountry,
    hide: true,
    minWidth: 150,
    valueGetter: (p: { data: TableRow }) =>
      orderOnlyValue(p, (data) => data.dropoff?.country),
    width: 150,
  },
  {
    colId: "dropoffState",
    headerName: TableHeadersName.DropoffState,
    hide: true,
    minWidth: 150,
    valueGetter: (p: { data: TableRow }) =>
      orderOnlyValue(p, (data) => data.dropoff?.state),
    width: 150,
  },
  {
    colId: "dropoffCity",
    headerName: TableHeadersName.DropoffCity,
    hide: true,
    minWidth: 170,
    valueGetter: (p: { data: TableRow }) =>
      orderOnlyValue(p, (data) => data.dropoff?.adminArea2),
    width: 170,
  },
  {
    cellRenderer: DateWindowCell,
    colId: "dropoffWindow",
    headerName: TableHeadersName.DropoffWindow,
    minWidth: 170,
    valueGetter: (p: { data: TableRow }) => {
      const rowData =
        p.data && p.data.type === "ROUTE_OPT_DROPOFF" ? p.data : null;

      if (!rowData) return null;
      return {
        end: new Date(rowData.scheduledEndDateTime),
        start: new Date(rowData.scheduledStartDateTime),
        timezone: rowData.timezone ?? "system",
      };
    },
    width: 170,
  },

  // Pickup Information
  {
    field: "pickup.name",
    headerName: TableHeadersName.PickupName,
    hide: true,
    minWidth: 150,
    width: 150,
  },
  {
    field: "pickup.phone",
    headerName: TableHeadersName.PickupPhone,
    hide: true,
    minWidth: 150,
    width: 150,
  },
  {
    cellClass: "ag-cell-value-style",
    field: "pickup.note",
    headerName: TableHeadersName.PickupNote,
    hide: true,
    minWidth: 150,
    width: 150,
  },
  {
    cellClass: "ag-cell-value-style",
    field: "pickup.businessName",
    headerName: TableHeadersName.PickupBusinessName,
    hide: true,
    minWidth: 150,
    width: 150,
  },
  {
    autoHeight: true,
    field: "pickup.address",
    headerName: TableHeadersName.PickupAddress,
    minWidth: 160,
    width: 200,
    wrapText: true,
  },
  {
    colId: "pickupCountry",
    headerName: TableHeadersName.PickupCountry,
    hide: true,
    minWidth: 150,
    valueGetter: (p: { data: TableRow }) =>
      orderOnlyValue(p, (data) => data.pickup?.country),
    width: 150,
  },
  {
    colId: "pickupState",
    headerName: TableHeadersName.PickupState,
    hide: true,
    minWidth: 150,
    valueGetter: (p: { data: TableRow }) =>
      orderOnlyValue(p, (data) => data.pickup?.state),
    width: 150,
  },
  {
    colId: "pickupCity",
    headerName: TableHeadersName.PickupCity,
    hide: true,
    minWidth: 170,
    valueGetter: (p: { data: TableRow }) =>
      orderOnlyValue(p, (data) => data.pickup?.adminArea2),
    width: 170,
  },

  {
    cellRenderer: DateWindowCell,
    colId: "pickupWindow",
    headerName: TableHeadersName.PickupWindow,
    minWidth: 170,

    valueGetter: (p: { data: TableRow }) => {
      const rowData =
        p.data && p.data.type === "ROUTE_OPT_PICKUP" ? p.data : null;
      if (!rowData) return null;
      return {
        end: new Date(rowData.scheduledEndDateTime),
        start: new Date(rowData.scheduledStartDateTime),
        timezone: rowData.timezone ?? "system",
      };
    },
    width: 170,
  },

  // Dropoff Requirements
  {
    field: "dropoffRequirement.signature",
    headerName: TableHeadersName.SignatureRequirement,
    hide: true,
  },
  {
    field: "dropoffRequirement.photo",
    headerName: TableHeadersName.ProofOfDeliveryRequirement,
    hide: true,
  },
  {
    field: "dropoffRequirement.note",
    headerName: TableHeadersName.NotesRequirement,
    hide: true,
  },

  // Order Details
  {
    field: "details.serviceDuration",
    headerName: TableHeadersName.ServiceTime,
    hide: true,
  },
  {
    cellClass: "ag-cell-value-style",
    field: "details.description",
    headerName: TableHeadersName.Description,
    hide: true,
    minWidth: 150,
    width: 150,
  },
  {
    field: "details.value",
    headerName: TableHeadersName.Value,
    hide: true,
    sortable: true,
  },
  {
    field: "details.tip",
    headerName: TableHeadersName.Tip,
    hide: true,
    sortable: true,
  },
  {
    field: "details.itemsCount",
    headerName: TableHeadersName.ItemsCount,
    hide: true,
    sortable: true,
  },
  {
    field: "details.length",
    headerName: TableHeadersName.Length,
    hide: true,
    sortable: true,
  },
  {
    field: "details.width",
    headerName: TableHeadersName.Width,
    hide: true,
    sortable: true,
  },
  {
    field: "details.height",
    headerName: TableHeadersName.Height,
    hide: true,
    sortable: true,
  },
  {
    field: "details.weight",
    headerName: TableHeadersName.Weight,
    hide: true,
    sortable: true,
  },
  {
    field: "details.distanceString",
    headerName: TableHeadersName.EstimatedDistance,
    hide: true,
  },
  {
    field: "details.driverRouteDistance",
    headerName: TableHeadersName.ActualDistance,
    hide: true,
    valueGetter: calculateRouteDistance,
  },

  {
    cellRenderer: StatusCell,
    field: "statusText",
    headerName: TableHeadersName.Status,
    minWidth: 150,
  },
  {
    cellRenderer: RouteDurationCell,
    field: "routeDuration",
    headerName: "Route Duration",
    minWidth: 150,
    width: 150,
  },
  {
    cellRenderer: ReportCountCell,
    field: "reportCount",
    headerName: "Reported Issues",
    minWidth: 130,
    width: 130,
  },
  {
    cellRenderer: PODCell,
    colId: "pod",
    headerName: TableHeadersName.POD,
    hide: true,
    minWidth: 180,
    width: 220,
  },
  {
    cellClass: "ag-cell-value-style",
    field: "statusDetails.reason",
    headerName: TableHeadersName.StatusReason,
    hide: true,
    minWidth: 150,
    width: 150,
  },
  {
    cellClass: "ag-cell-value-style",
    field: "statusDetails.note",
    headerName: TableHeadersName.DriverNote,
    hide: true,
    minWidth: 150,
    width: 150,
  },
  {
    field: "minVehicle",
    headerName: TableHeadersName.VehicleType,
    hide: true,
    minWidth: 100,
    width: 100,
  },
  {
    cellRenderer: DateCell,
    cellRendererParams: dateCellParams,
    field: "scheduledStartDateTime",
    headerName: TableHeadersName.ScheduledStartTime,
    minWidth: 200,
    sortable: true,
  },
  {
    cellRenderer: DateCell,
    cellRendererParams: dateCellParams,
    field: "scheduledEndDateTime",
    headerName: TableHeadersName.ScheduledEndTime,
    minWidth: 200,
    sortable: true,
  },
  {
    cellRenderer: DateCell,
    cellRendererParams: dateCellParams,
    field: "actualStartDateTime",
    headerName: TableHeadersName.InTransit,
    minWidth: 200,
    sortable: true,
  },
  {
    cellRenderer: DateCell,
    cellRendererParams: dateCellParams,
    field: "geoPickupDateTime",
    headerName: TableHeadersName.PickupArrivalTime,
    minWidth: 200,
    sortable: true,
  },
  {
    cellRenderer: DateCell,
    cellRendererParams: dateCellParams,
    field: "actualPickupDateTime",
    headerName: TableHeadersName.ActualPickupTime,
    minWidth: 200,
    sortable: true,
  },
  {
    cellRenderer: DateCell,
    cellRendererParams: dateCellParams,
    field: "geoDropoffDateTime",
    headerName: TableHeadersName.DropoffArrivalTime,
    minWidth: 200,
    sortable: true,
  },
  {
    cellRenderer: DateCell,
    cellRendererParams: dateCellParams,
    field: "actualEndDateTime",
    headerName: TableHeadersName.ActualDropOffTime,
    minWidth: 200,
    sortable: true,
  },
  {
    colId: "timezone",
    headerName: TableHeadersName.Timezone,
    minWidth: 150,
    valueGetter: (p: { data: TableRow }) => {
      const rowData = p.data;
      if (!rowData || !rowData.timezone) {
        return null;
      }

      if (rowData.timezone !== "system") {
        return dayjs().tz(rowData.timezone).format("z");
      }
      return "";
    },
  },
  {
    cellRenderer: DateCell,
    cellRendererParams: dateCellParams,
    field: "estimatedEtaDateTime",
    headerName: TableHeadersName.ETAAtCreation,
    minWidth: 150,
  },
];

export const getInitialTableData = (
  customFields?: CustomField[],
  formFields?: OrderFormField[],
): ColDef[] => {
  let columnDefs = INITIAL_TABLE_DATA;

  if (customFields?.length) {
    const customFieldDefs: ColDef[] = customFields.map((customField) => ({
      autoHeight: customField.type === "DROPDOWN" && customField.isMulti,
      cellRenderer: CustomFieldCell,
      colId: `${CUSTOM_FIELD_COL_ID_PREFIX}${customField.id}`,
      headerName: customField.name,
      hide: true,
      minWidth: 150,
      valueGetter: (p: { data: Order }) => {
        const customFieldData = p.data?.customFields?.find(
          (item) => item.definition.id === customField.id,
        );
        return customFieldData?.values?.length
          ? customFieldData?.values?.map((item) => item.value)?.join(",")
          : customFieldData?.definition?.defaultValues
              ?.map((item) => item?.value)
              ?.join(",");
      },
      wrapText: customField.type === "DROPDOWN" && customField.isMulti,
    }));

    columnDefs = [...INITIAL_TABLE_DATA, ...customFieldDefs];
  }

  return applyFormConfigColumnOrder(columnDefs, formFields);
};

export const routeNameValueGenerator = (p: { data: TableRow }) => {
  if (p.data.entity === "order") {
    return p.data.displayId;
  }
  return p.data?.externalId?.length ? p.data.externalId : p.data?.displayId;
};
